'use client';

import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard } from '@capacitor/keyboard';

/**
 * Detects if the app is running inside a native Capacitor shell
 */
export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Returns 'ios' | 'android' | 'web'
 */
export function getPlatform(): string {
  return Capacitor.getPlatform();
}

/**
 * Override the safe-area CSS variables based on whether WKWebView is
 * already inset (handling the safe-area itself) or fills the full screen
 * (CSS must handle it).
 *
 * Detection: if the WebView's `window.innerHeight` is smaller than the
 * device `screen.height`, WKWebView is using `contentInset: 'automatic'`
 * (or the older default) and has already clipped its frame to clear the
 * status bar / home indicator. In that case adding env(safe-area-inset-*)
 * padding in CSS would double-count and create the empty gap users see
 * below the header and above the bottom nav. So we zero the vars out.
 *
 * If the WebView fills the screen (`contentInset: 'never'`), we keep the
 * env()-driven values (read from a probe so the same number is used
 * everywhere).
 */
function syncNativeSafeAreaInsets() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const root = document.documentElement;
  const probe = document.createElement('div');
  probe.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    'visibility:hidden',
    'pointer-events:none',
    'padding-top:env(safe-area-inset-top)',
    'padding-bottom:env(safe-area-inset-bottom)',
    'padding-left:env(safe-area-inset-left)',
    'padding-right:env(safe-area-inset-right)',
  ].join(';');
  document.body.appendChild(probe);
  const cs = getComputedStyle(probe);
  const envTop = parseFloat(cs.paddingTop) || 0;
  const envBottom = parseFloat(cs.paddingBottom) || 0;
  const envLeft = parseFloat(cs.paddingLeft) || 0;
  const envRight = parseFloat(cs.paddingRight) || 0;
  document.body.removeChild(probe);

  const screenHeight = window.screen.height;
  const viewportHeight = window.innerHeight;
  // > 4px slack avoids false positives from rounding / 1-px borders.
  const webViewIsAlreadyInset = screenHeight - viewportHeight > 4;

  const top = webViewIsAlreadyInset ? 0 : envTop;
  const bottom = webViewIsAlreadyInset ? 0 : envBottom;

  root.style.setProperty('--safe-area-inset-top', `${top}px`);
  root.style.setProperty('--safe-area-inset-bottom', `${bottom}px`);
  root.style.setProperty('--safe-area-inset-left', `${envLeft}px`);
  root.style.setProperty('--safe-area-inset-right', `${envRight}px`);
  root.style.setProperty('--safe-area-top', `${top}px`);
  root.style.setProperty('--safe-area-bottom', `${bottom}px`);
}

/**
 * Lightweight, read-only hook returning whether we're inside the native
 * Capacitor shell and which platform. Safe to call from many components at
 * once — it registers NO global listeners and triggers NO side effects.
 *
 * The one-time native initialization (status bar, splash, keyboard, Android
 * back button) lives in `useNativeInit`, which must be mounted exactly once
 * (via NativeProvider). Doing the init here instead would register a fresh
 * Android `backButton` listener for every component that just wants to read
 * `isNative` — and each listener fires `window.history.back()` on a single
 * hardware back press, popping several history entries at once.
 */
export function useNativeApp() {
  const [isNative, setIsNative] = useState(false);
  const [platform, setPlatform] = useState<string>('web');

  useEffect(() => {
    setIsNative(Capacitor.isNativePlatform());
    setPlatform(Capacitor.getPlatform());
  }, []);

  return { isNative, platform };
}

// Guards the one-time native init against double-invocation (multiple
// providers, React StrictMode remounts, fast-refresh in dev).
let nativeInitialized = false;

/**
 * Runs the one-time native plugin setup. Must be mounted exactly once,
 * from NativeProvider near the app root.
 */
export function useNativeInit() {
  useEffect(() => {
    const native = Capacitor.isNativePlatform();

    // Web build: skip every native-only plugin call. Calling Keyboard /
    // SplashScreen / StatusBar etc. on web triggers "plugin not implemented
    // in web" warnings, even when wrapped in try/catch.
    if (!native) return;

    // Only ever initialize once across the whole app lifetime.
    if (nativeInitialized) return;
    nativeInitialized = true;

    let cleanup: (() => void) | undefined;

    const initializeNative = async () => {
      const platformName = Capacitor.getPlatform();

      // Tag <html> so global CSS can apply hard-coded native safe-area
      // insets (Android WebView often returns 0 for env(safe-area-inset-*)).
      if (typeof document !== 'undefined') {
        document.documentElement.classList.add('native-app');
        if (platformName === 'android') {
          document.documentElement.classList.add('native-android');
        } else if (platformName === 'ios') {
          document.documentElement.classList.add('native-ios');
        }
      }

      // Configure Status Bar
      StatusBar.setStyle({ style: Style.Light }).catch(() => {});
      if (platformName === 'android') {
        StatusBar.setBackgroundColor({ color: '#5d18ea' }).catch(() => {});
        // Stop the WebView from drawing behind the status bar so the header
        // doesn't overlap the battery/network icons. The status bar stays
        // purple (#5d18ea) and the WebView starts cleanly below it.
        await StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
      }

      // Override the env()-driven safe-area variables with measured values
      // (see syncNativeSafeAreaInsets docstring for why). Re-measure on
      // rotation/resize so split-view & orientation changes still match.
      syncNativeSafeAreaInsets();
      window.addEventListener('resize', syncNativeSafeAreaInsets);
      window.addEventListener('orientationchange', syncNativeSafeAreaInsets);
      window.visualViewport?.addEventListener('resize', syncNativeSafeAreaInsets);

      // Hide splash screen after app is ready
      SplashScreen.hide({ fadeOutDuration: 300 }).catch(() => {});

      // Android hardware back button / back gesture — navigate within app
      // instead of exiting. Capacitor's App plugin fires `backButton` (not the
      // legacy Cordova `backbutton` DOM event), passing `canGoBack` from the
      // WebView's history. Without this listener Android falls back to its
      // default behaviour of closing the app on the first back press.
      let lastBackPress = 0;
      const backButtonListener = await App.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack || window.history.length > 1) {
          window.history.back();
        } else {
          // At the app root: require a second back press within 2s to exit.
          const now = Date.now();
          if (now - lastBackPress < 2000) {
            App.exitApp();
          } else {
            lastBackPress = now;
          }
        }
      });

      // Keyboard listeners for better UX on native
      const showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
        document.body.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
        document.body.classList.add('keyboard-open');
      });
      const hideListener = await Keyboard.addListener('keyboardWillHide', () => {
        document.body.style.setProperty('--keyboard-height', '0px');
        document.body.classList.remove('keyboard-open');
        syncNativeSafeAreaInsets();
      });

      cleanup = () => {
        backButtonListener.remove();
        window.removeEventListener('resize', syncNativeSafeAreaInsets);
        window.removeEventListener('orientationchange', syncNativeSafeAreaInsets);
        window.visualViewport?.removeEventListener('resize', syncNativeSafeAreaInsets);
        showListener.remove();
        hideListener.remove();
        // Allow a future mount to re-initialize cleanly.
        nativeInitialized = false;
      };
    };

    initializeNative();

    return () => {
      cleanup?.();
    };
  }, []);
}
